import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, Space, Tabs, Typography, message } from 'antd';
import {
  getAgreementTerms,
  updateAgreementTerms,
  getAgreementPrivacy,
  updateAgreementPrivacy,
  type AdminAgreement,
} from '../services/api';

const { TextArea } = Input;

const AgreementConfig: React.FC = () => {
  const [termsForm] = Form.useForm();
  const [privacyForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [activeTab, setActiveTab] = useState('terms');

  const loadContent = useCallback(async (type: 'terms' | 'privacy') => {
    setLoading(true);
    try {
      const fetcher = type === 'terms' ? getAgreementTerms : getAgreementPrivacy;
      const res = await fetcher();
      if (res.code !== 0) {
        message.error(res.message || `加载${type === 'terms' ? '用户条款' : '隐私条款'}失败`);
        return;
      }
      const data = res.data as AdminAgreement | null;
      const form = type === 'terms' ? termsForm : privacyForm;
      form.setFieldsValue({
        content: data?.content || '',
        update_date: data?.update_date || '',
      });
    } catch (error) {
      message.error(`加载${type === 'terms' ? '用户条款' : '隐私条款'}失败`);
    } finally {
      setLoading(false);
    }
  }, [termsForm, privacyForm]);

  useEffect(() => {
    void loadContent('terms');
    void loadContent('privacy');
  }, [loadContent]);

  const handleSave = async (type: 'terms' | 'privacy') => {
    const form = type === 'terms' ? termsForm : privacyForm;
    const setSaving = type === 'terms' ? setSavingTerms : setSavingPrivacy;

    try {
      const values = await form.validateFields();
      setSaving(true);

      const updater = type === 'terms' ? updateAgreementTerms : updateAgreementPrivacy;
      const res = await updater({
        content: values.content || '',
        update_date: values.update_date || undefined,
      });

      if (res.code !== 0) {
        message.error(res.message || `保存${type === 'terms' ? '用户条款' : '隐私条款'}失败`);
        return;
      }

      message.success(`${type === 'terms' ? '用户条款' : '隐私条款'}已保存`);
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        // 表单校验失败，不提示
        return;
      }
      message.error(`保存${type === 'terms' ? '用户条款' : '隐私条款'}失败`);
    } finally {
      setSaving(false);
    }
  };

  const tabItems = [
    {
      key: 'terms',
      label: '用户条款',
      children: (
        <Card loading={loading}>
          <Form
            form={termsForm}
            layout="vertical"
            style={{ maxWidth: 900 }}
          >
            <Form.Item
              label="更新日期（显示用）"
              name="update_date"
              extra="例如：2026年6月1日。留空则自动使用当前日期。"
            >
              <Input placeholder="2026年6月1日" style={{ maxWidth: 300 }} />
            </Form.Item>

            <Form.Item
              label="条款内容"
              name="content"
              rules={[{ required: true, message: '请输入用户条款内容' }]}
              extra="支持按段落编辑，每个段落之间用空行分隔。保存后 App 端会同步更新。"
            >
              <TextArea
                rows={22}
                placeholder="请输入用户条款内容..."
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button
                  type="primary"
                  loading={savingTerms}
                  onClick={() => handleSave('terms')}
                >
                  保存用户条款
                </Button>
                <Button onClick={() => loadContent('terms')}>
                  重新加载
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: 'privacy',
      label: '隐私条款',
      children: (
        <Card loading={loading}>
          <Form
            form={privacyForm}
            layout="vertical"
            style={{ maxWidth: 900 }}
          >
            <Form.Item
              label="更新日期（显示用）"
              name="update_date"
              extra="例如：2026年6月1日。留空则自动使用当前日期。"
            >
              <Input placeholder="2026年6月1日" style={{ maxWidth: 300 }} />
            </Form.Item>

            <Form.Item
              label="条款内容"
              name="content"
              rules={[{ required: true, message: '请输入隐私条款内容' }]}
              extra="支持按段落编辑，每个段落之间用空行分隔。保存后 App 端会同步更新。"
            >
              <TextArea
                rows={22}
                placeholder="请输入隐私条款内容..."
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button
                  type="primary"
                  loading={savingPrivacy}
                  onClick={() => handleSave('privacy')}
                >
                  保存隐私条款
                </Button>
                <Button onClick={() => loadContent('privacy')}>
                  重新加载
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        条款内容管理
      </Typography.Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </div>
  );
};

export default AgreementConfig;